import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import { initializeAnalytics } from './utils/analytics';
import { hydrateAuthFromNativeStorage } from './utils/authStorage';
import { hydrateLegalFromNativeStorage } from './utils/legalStorage';

// CRITICAL: Set paymentSuccessProcessed flag IMMEDIATELY if we're on payment-success
// This must run BEFORE React loads to prevent getLaunchUrl() from running
// This prevents the infinite loop where getLaunchUrl() keeps returning the same URL
if (typeof window !== 'undefined') {
  const currentPath = window.location.pathname;
  const currentSearch = window.location.search;
  const currentHref = window.location.href;
  
  // Check multiple indicators of payment-success
  const isPaymentSuccess = 
    currentPath === '/payment-success' ||
    currentSearch.includes('session_id') ||
    currentHref.includes('payment-success') ||
    currentHref.includes('session_id');
  
  if (isPaymentSuccess) {
    console.log('🛑🚨 MAIN.JSX: Payment-success detected - setting flags IMMEDIATELY before React loads 🚨');
    console.log('🛑 MAIN.JSX details:', { currentPath, currentSearch: currentSearch.substring(0, 100) });
    localStorage.setItem('paymentSuccessProcessed', 'true');
    localStorage.setItem('launchUrlChecked', 'true');
    // Also mark the URL as processed
    try {
      const params = new URLSearchParams(currentSearch);
      const sessionId = params.get('session_id') || '';
      if (sessionId) {
        const processedUrls = JSON.parse(localStorage.getItem('processedDeepLinks') || '[]');
        const normalizedKey = `/payment-success?session_id=${sessionId}`;
        if (!processedUrls.includes(normalizedKey)) {
          processedUrls.push(normalizedKey);
          localStorage.setItem('processedDeepLinks', JSON.stringify(processedUrls));
        }
      }
    } catch (e) {
      console.warn('⚠️ MAIN.JSX: Could not mark URL as processed:', e);
    }
  }
}

// Initialize analytics on app load (will check consent internally)
initializeAnalytics();

// Register Service Worker for PWA (web only — SW on Capacitor Android can cache stale shells and cause blank screens)
const isNativeCapacitorApp =
  typeof window !== 'undefined' &&
  (window.Capacitor?.getPlatform?.() === 'ios' ||
    window.Capacitor?.getPlatform?.() === 'android');

if ('serviceWorker' in navigator && !isNativeCapacitorApp) {
  // Reload only when an *existing* SW is replaced (deploy update). Skip reload on first
  // install so Accept all / cookie consent is not interrupted by controllerchange.
  const hadControllerOnLoad = !!navigator.serviceWorker.controller;
  let reloadingForSwUpdate = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloadingForSwUpdate || !hadControllerOnLoad) return;
    reloadingForSwUpdate = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then(async (registration) => {
        console.log('[PWA] Service Worker registered successfully:', registration.scope);
        
        // Register periodic background sync for offline queue
        if ('sync' in registration) {
          try {
            await registration.sync.register('sync-offline-queue');
            console.log('[PWA] Background sync registered for offline queue');
          } catch (error) {
            console.log('[PWA] Background sync not available:', error);
          }
        }
        
        // Pre-cache Tesseract.js resources for offline OCR (if online)
        if (navigator.onLine) {
          try {
            const { precacheTesseractResources } = await import('./utils/offlineQueue');
            // Delay pre-caching slightly to not block app load
            setTimeout(async () => {
              await precacheTesseractResources();
            }, 2000);
          } catch (error) {
            console.log('[PWA] Could not pre-cache Tesseract resources:', error);
          }
        }
        
        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 60000); // Check every minute
        
        // Handle updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker available, prompt user to refresh
                console.log('[PWA] New service worker available. Refresh to update.');
                // You can show a toast/notification here to prompt user to refresh
              }
            });
          }
        });
      })
      .catch((error) => {
        console.error('[PWA] Service Worker registration failed:', error);
      });
  });
}

// Debug: Check CSP header (development only)
if (typeof window !== 'undefined' && (import.meta.env.DEV || import.meta.env.MODE === 'development')) {
  // Check if CSP meta tag exists
  const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  if (cspMeta) {
    const cspContent = cspMeta.getAttribute('content');
    console.log('🔒 CSP Meta Tag Found:', cspContent);
    const hasWorkerSrc = cspContent.includes('worker-src');
    const hasBlob = cspContent.includes('blob:');
    console.log('🔍 CSP Analysis:', {
      hasWorkerSrc,
      hasBlob,
      workerSrcValue: hasWorkerSrc ? cspContent.match(/worker-src[^;]+/)?.[0] : 'NOT FOUND'
    });
    
    if (!hasWorkerSrc || !hasBlob) {
      console.error('❌ CSP Meta Tag is MISSING worker-src with blob:!');
    } else {
      console.log('✅ CSP Meta Tag includes worker-src with blob:');
    }
  } else {
    console.error('⚠️ No CSP meta tag found in HTML!');
  }
  
  // Note: HTTP headers can't be read via JavaScript, but we can check for violations
  console.log('📋 To check HTTP CSP header:');
  console.log('   1. Open DevTools → Network tab');
  console.log('   2. Reload page');
  console.log('   3. Click the first request (HTML document)');
  console.log('   4. Go to "Headers" tab → "Response Headers"');
  console.log('   5. Look for "Content-Security-Policy"');
  console.log('   ⚠️ If HTTP header exists, it OVERRIDES the meta tag!');
}

// On mobile: restore auth from native storage before first render so user stays logged in.
// Use initialUser from hydrate (from Preferences) so we don't rely on localStorage read after full close
// — on cold start the WebView's localStorage can be empty or not yet visible.
//
// CRITICAL: Do not static-import App (or anything that imports lib/supabase.js) before hydrate completes.
// Otherwise GoTrue initializes with empty localStorage, misses the session copied from Preferences, and
// cold-start refresh can fail → false "session expired" after killing the app (common on Android emulator).

/** App opened via https App Link with ?hash= (tokens); Supabase expects URL #fragment — normalize before React. */
function normalizeLoginHashQueryToFragment() {
  if (typeof window === 'undefined') return;
  try {
    const u = new URL(window.location.href);
    const path = u.pathname.replace(/\/$/, '') || '/';
    if (path !== '/login' && !path.endsWith('/login')) return;
    const qpHash = u.searchParams.get('hash');
    if (!qpHash || u.hash) return;
    let inner = qpHash;
    try {
      inner = decodeURIComponent(qpHash);
    } catch (_) {
      /* use raw */
    }
    if (!inner.includes('access_token=')) return;
    u.searchParams.delete('hash');
    const q = u.searchParams.toString();
    const newUrl = u.pathname + (q ? `?${q}` : '') + '#' + inner;
    window.history.replaceState(null, '', newUrl);
  } catch (_) {
    /* ignore */
  }
}

async function init() {
  normalizeLoginHashQueryToFragment();
  const { authLog } = await import('./utils/authDiagnostics');
  authLog('main', 'init start');
  const hydrateResult = await hydrateAuthFromNativeStorage();
  await hydrateLegalFromNativeStorage(); // Persist legal consent across app restarts (iOS/Android)
  authLog('main', 'hydrate done', { restored: hydrateResult.restored, hasInitialUser: !!hydrateResult.initialUser, email: hydrateResult.initialUser?.email });

  // Mobile: Fail fast when session is dead. If token expired, try refresh (with retry on timeout).
  // Android cold start: network often not ready yet — use longer timeout and retry so we don't falsely log out.
  const isCapacitor = typeof window !== 'undefined' && (
    (window.Capacitor?.getPlatform && ['ios', 'android'].includes(window.Capacitor.getPlatform())) ||
    (window.location?.protocol && window.location.protocol !== 'http:' && window.location.protocol !== 'https:')
  );
  if (isCapacitor && hydrateResult.restored && typeof window !== 'undefined') {
    const { isJWTExpired, markSessionDead, isSessionDead } = await import('./utils/iosSessionRefresh');
    const { clearAuthFromNativeStorage } = await import('./utils/authStorage');
    if (isSessionDead()) {
      authLog('main', 'session was marked dead, clearing');
      try {
        localStorage.removeItem('trackio-auth-token');
        await clearAuthFromNativeStorage();
      } catch (_) {}
    } else {
      const raw = localStorage.getItem('trackio-auth-token');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          const token = parsed?.access_token;
          if (token && isJWTExpired(token)) {
            authLog('main', 'token expired on cold start, trying refresh (with retry for Android)');
            const { supabase } = await import('./lib/supabase');
            const REFRESH_TIMEOUT_MS = 10000; // 10s — Android cold start network can be slow
            let lastError = null;
            for (let attempt = 0; attempt < 2; attempt++) {
              if (attempt > 0) {
                authLog('main', 'cold start refresh retry after timeout');
                await new Promise((r) => setTimeout(r, 2000)); // wait for network
              }
              const result = await Promise.race([
                supabase.auth.refreshSession(),
                new Promise((_, r) => setTimeout(() => r({ data: { session: null }, error: { code: 'refresh_timeout' } }), REFRESH_TIMEOUT_MS))
              ]);
              const { data, error } = result ?? {};
              // refresh_token_not_found: retry once (align with iosSessionRefresh) — single glitch can spuriously log out.
              if (error?.code === 'refresh_token_not_found') {
                if (attempt === 1) {
                  authLog('main', 'refresh_token_not_found after retry, clearing session');
                  markSessionDead();
                  localStorage.removeItem('trackio-auth-token');
                  await clearAuthFromNativeStorage();
                  try { sessionStorage.setItem('session_expired_reason', 'true'); } catch (_) {}
                  break;
                }
                authLog('main', 'refresh_token_not_found on cold start attempt 1, retrying');
                await new Promise((r) => setTimeout(r, 2000));
                continue;
              }
              if (data?.session) {
                authLog('main', 'refresh success on cold start', { attempt: attempt + 1 });
                localStorage.setItem('trackio-auth-token', JSON.stringify(data.session));
                await import('./utils/authStorage').then(m => m.persistAuthToNativeStorage?.());
                break;
              }
              lastError = error;
              // Timeout or other error — retry once (don't clear yet)
            }
            if (lastError?.code === 'refresh_timeout') {
              // Both attempts timed out — keep user logged in; proactive refresh will retry when network is ready
              authLog('main', 'refresh timed out twice, keeping user (will retry in background)');
            }
          }
        } catch (_) {}
      }
    }
  }

  // iOS / Android: sync native store subscription on launch so renewals/expiry match DB before limits run
  const capPlat = typeof window !== 'undefined' && window.Capacitor?.getPlatform?.();
  if ((capPlat === 'ios' || capPlat === 'android') && hydrateResult.restored) {
    const email =
      hydrateResult.initialUser?.email ||
      (() => {
        try {
          const raw = localStorage.getItem('trackio-auth-token');
          if (!raw) return null;
          const t = JSON.parse(raw)?.access_token;
          if (!t) return null;
          return JSON.parse(atob(t.split('.')[1]))?.email ?? null;
        } catch {
          return null;
        }
      })();
    if (email) {
      try {
        const {
          syncAppleSubscriptionWithBackend,
          syncGoogleSubscriptionWithBackend,
        } = await import('./services/iosIAPService');
        if (capPlat === 'ios') {
          await syncAppleSubscriptionWithBackend(email);
        } else {
          await syncGoogleSubscriptionWithBackend(email);
        }
        if (typeof window !== 'undefined') {
          window.setTimeout(() => {
            window.dispatchEvent(new CustomEvent('forceSubscriptionSync', { detail: { force: true } }));
          }, 1500);
        }
      } catch (_) {}
    }
  }

  // Set initialUser only if we still have a session (we didn't clear it in fail-fast).
  if (typeof window !== 'undefined') {
    const raw = localStorage.getItem('trackio-auth-token');
    if (raw) {
      if (hydrateResult.initialUser) {
        window.__TRACKIO_INITIAL_USER__ = hydrateResult.initialUser;
        authLog('main', 'set initialUser from hydrate', { email: hydrateResult.initialUser?.email });
      } else {
        try {
          const parsed = JSON.parse(raw);
          const token = parsed?.access_token;
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const email = payload?.email ?? payload?.user_email;
            if (email) {
              window.__TRACKIO_INITIAL_USER__ = { email, businessName: '', role: 'user' };
              authLog('main', 'set initialUser from fallback', { email });
            }
          }
        } catch (_) {}
      }
    }
  }
  authLog('main', 'about to render', { hasInitialUser: !!window.__TRACKIO_INITIAL_USER__ });

  const { default: App } = await import('./App');

  // Apply capacitor-app class for mobile layout (fixed height, overflow hidden)
  // Must use same logic as platform.js: Android uses https scheme, so check Capacitor.getPlatform()
  if (typeof window !== 'undefined') {
    let isNative = false;
    if (window.Capacitor?.getPlatform) {
      const p = window.Capacitor.getPlatform();
      isNative = p === 'ios' || p === 'android';
    } else {
      const proto = window.location.protocol;
      isNative = proto !== 'http:' && proto !== 'https:';
    }
    if (isNative) {
      document.documentElement.classList.add('capacitor-app');
      document.body.classList.add('capacitor-app');
      const capPlatform = window.Capacitor?.getPlatform?.();
      if (capPlatform === 'android') {
        document.documentElement.classList.add('capacitor-android');
        document.body.classList.add('capacitor-android');
      }
      if (capPlatform === 'ios') {
        document.documentElement.classList.add('capacitor-ios');
        document.body.classList.add('capacitor-ios');
      }
    }
  }

  createRoot(document.getElementById('root')).render(
    <StrictMode>
      <HelmetProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </HelmetProvider>
    </StrictMode>
  );

  requestAnimationFrame(() => {
    void import('./utils/hideNativeSplash.js').then((m) => m.hideNativeSplash());
  });
}
init();